
import { cn } from '../../lib/utils'
import { getInitials } from '@airclou/utils'

export interface GroupAvatarProps {
  /** The group name to generate initials from */
  name: string
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'profile' | 'xl' | '2xl'
  /** Color variant */
  variant?: 'default' | 'gray' | 'blue' | 'green' | 'red' | 'yellow' | 'purple'
  /** Additional CSS classes */
  className?: string
  /** Custom background color (overrides variant) */
  bgColor?: string
  /** Custom text color (overrides variant) */
  textColor?: string
}

const sizeClasses = {
  xs: 'w-5 h-5 text-xs',
  sm: 'w-6 h-6 text-xs',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
  profile: 'w-11 h-11 text-sm',
  xl: 'w-12 h-12 text-sm',
  '2xl': 'w-16 h-16 text-base'
}

const variantClasses = {
  default: 'bg-primary text-primary-foreground',
  gray: 'bg-gray-300 dark:bg-gray-400 text-gray-700 dark:text-gray-900',
  blue: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
  green: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
  red: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
  yellow: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
  purple: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
}

/**
 * A reusable group avatar component that displays group initials in a rounded rectangle.
 * Uses rounded rectangles to distinguish from circular person avatars.
 * Supports multiple sizes and color variants for different contexts.
 * 
 * Features:
 * - Multiple size options (xs, sm, md, lg, xl)
 * - Rounded rectangle shape for group distinction
 * - Color variants with proper dark mode support
 * - Consistent initials generation (up to 2 characters)
 * - Flexible styling with custom colors
 * 
 * @example
 * ```tsx
 * // In group cards
 * <GroupAvatar name="Engineering Team" size="lg" />
 * 
 * // In headers
 * <GroupAvatar name="Sales Department" size="xl" variant="green" />
 * 
 * // Custom colors
 * <GroupAvatar 
 *   name="Marketing Team" 
 *   size="md" 
 *   bgColor="#f59e0b" 
 *   textColor="white" 
 * />
 * ```
 */
export function GroupAvatar({
  name,
  size = 'md',
  variant = 'default',
  className,
  bgColor,
  textColor
}: GroupAvatarProps) {
  const initials = getInitials(name)
  const sizeClass = sizeClasses[size]
  const variantClass = variantClasses[variant]

  const avatarClasses = cn(
    // Base styling with rounded rectangle shape
    'rounded-lg flex items-center justify-center font-semibold flex-shrink-0',
    // Apply size classes
    sizeClass,
    // Apply variant colors if no custom colors provided
    !bgColor && !textColor && variantClass,
    className
  )

  const customStyles = {
    ...(bgColor && { backgroundColor: bgColor }),
    ...(textColor && { color: textColor })
  }

  return (
    <div
      className={avatarClasses}
      style={customStyles}
      title={name}
    >
      <span>{initials}</span>
    </div>
  )
}

export default GroupAvatar 